import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch,
  Space, Popconfirm, message, Card, Tag, Select, InputNumber, Tooltip
} from 'antd';
import { PlusOutlined, DeleteOutlined, LoginOutlined, LockOutlined, WarningOutlined, EyeOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword } from '../api/users';
import { getBranches } from '../api/branches';
import useDebounce from '../hooks/useDebounce';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import useServerSort from '../hooks/useServerSort';
import useAuthStore from '../store/authStore';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { roleLevel } from '../utils/roleLevel';
import { toEmailLocalPart } from '../utils/emailLocalPart';
import PhoneInput from '../components/common/PhoneInput';
import { apiErrorMessage } from '../utils/apiError';
import { formatPhone } from '../utils/phone';
import { VeliPortalRozeti } from '../components/common/VeliPortalRozeti';

const { Option } = Select;

/**
 * Doğum gününe kaç gün kaldı (yıl yok sayılır, 0 = bugün).
 * Backend'deki `UserController::kacGunKaldi()` ile aynı kural — listedeki
 * renk ile bildirimlerin aynı günü işaret etmesi için.
 */
const kacGunKaldi = (birthDate) => {
  const bugun = dayjs().startOf('day');
  let buYil = dayjs(birthDate).year(bugun.year()).startOf('day');
  if (buYil.isBefore(bugun)) buYil = buYil.add(1, 'year');
  return buYil.diff(bugun, 'day');
};

// Veli e-posta domaini icin kullanilan degisken burada da kullanilyor -
// personel/veli fark etmeksizin tum kullanicilar bu modaldan olusturuluyor
// ve ikisi de ayni domain'i (aliagarobotik.com) kullaniyor.
const EMAIL_DOMAIN = process.env.REACT_APP_PARENT_EMAIL_DOMAIN || null;

const roleLabels = {
  developer_admin: { label: 'Geliştirici Admin', color: 'purple' },
  super_admin: { label: 'Süper Admin', color: 'red' },
  branch_manager: { label: 'Şube Müdürü', color: 'blue' },
  teacher: { label: 'Öğretmen', color: 'green' },
  stajyer: { label: 'Stajyer', color: 'cyan' },
  parent: { label: 'Veli', color: 'orange' },
};

const Users = () => {
  const { user: currentUser, startImpersonation } = useAuthStore();
  const navigate = useNavigate();
  const isDeveloperAdmin = currentUser?.role === 'developer_admin';
  const canImpersonate   = isDeveloperAdmin || currentUser?.role === 'super_admin';

  const canResetPasswordOf = (target) => {
    if (target?.role === 'developer_admin') return isDeveloperAdmin;
    if (['super_admin', 'developer_admin'].includes(currentUser?.role)) return true;
    return (roleLevel[currentUser?.role] ?? 0) > (roleLevel[target?.role] ?? 0);
  };

  const handleResetPassword = async (targetUser) => {
    try {
      const res = await resetUserPassword(targetUser.id);

      // VELİDE ŞİFRE DÖNMEZ. Veli hesabında sıfırlama, sabit bir şifre atamak
      // değil AKTİFLEŞTİRMEYİ GERİ ALMAK anlamına geliyor — portal ~500 veliye
      // açıldı ve tahmin edilebilir şifreyi ortadan kaldırmak için etkinleştirme
      // akışı yazıldı; buradan şifre vermek onu arka kapıdan geri açardı.
      // Personelde eski davranış aynen sürüyor.
      if (res.data.activation_reset) {
        Modal.success({
          title: 'Aktifleştirme Sıfırlandı',
          content: (
            <div>
              <p>{targetUser.name} {targetUser.surname} adlı velinin aktifleştirmesi sıfırlandı.</p>
              <p>
                Veli, giriş ekranındaki <strong>"Üyeliğimi Aktifleştir"</strong> bağlantısından
                telefon numarası ve çocuğunun T.C. kimlik numarasıyla yeni şifresini
                kendisi belirleyecek.
              </p>
              <p className="text-gray-400 text-sm">
                Ona iletmeniz gereken bir şifre yok.
              </p>
            </div>
          ),
        });
        return;
      }

      Modal.success({
        title: 'Şifre Sıfırlandı',
        content: (
          <div>
            <p>{targetUser.name} {targetUser.surname} kullanıcısının şifresi varsayılan değere sıfırlandı.</p>
            <p>Yeni şifre: <strong>{res.data.default_password}</strong></p>
            <p className="text-gray-400 text-sm">Kullanıcı ilk girişte bu şifreyi değiştirmek zorunda kalacak.</p>
          </div>
        ),
      });
    } catch (error) {
      message.error(apiErrorMessage(error, 'Şifre sıfırlanamadı.'));
    }
  };

  const handleImpersonate = async (targetUser) => {
    try {
      const res = await api.post(`/admin/impersonate/${targetUser.id}`);
      startImpersonation(res.data.user, res.data.impersonation_id);
      message.success(`${targetUser.name} ${targetUser.surname} olarak giriş yapıldı.`);
      navigate('/dashboard');
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(null);
  const [branchFilter, setBranchFilter] = useState(null);
  // Veli portal durumu - listedeki rozetin filtresi. Yalnizca velide
  // anlamli, backend de rol kisitini kendisi uyguluyor.
  const [portalFilter, setPortalFilter] = useState(null);
  // Ogrenci durumu - velinin cocugu hala bizimle mi (durum + donem kaydi).
  const [ogrenciFilter, setOgrenciFilter] = useState(null);
  const debouncedSearch = useDebounce(search, 400);
  const [form] = Form.useForm();
  const [emailTouched, setEmailTouched] = useState(false);
  const isAutoFillingEmail = useRef(false);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('users', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();

  // `portal` EN SONA eklendi ve varsayılanı state'ten okuyor: böylece mevcut
  // çağrıların hiçbirine dokunmak gerekmedi. fetchUsers bir useCallback
  // DEĞİL (her render'da yeniden tanımlanıyor), dolayısıyla varsayılan
  // parametre bayat kapanış okumuyor — CLAUDE.md'deki stale-closure tuzağı
  // burada geçerli değil.
  const fetchUsers = async (page = 1, searchText = debouncedSearch, role = roleFilter, branch = branchFilter, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null, portal = portalFilter, ogrenci = ogrenciFilter) => {
    setLoading(true);
    try {
      const response = await getUsers({
        page,
        per_page: perPage,
        search: searchText,
        role,
        branch_id: branch,
        portal_durumu: portal || undefined,
        ogrenci_durumu: ogrenci || undefined,
        sort_by: sortBy || undefined,
        sort_direction: sortDir || undefined,
      });
      setUsers(response.data.data);
      setPagination({
        current: response.data.current_page,
        pageSize: response.data.per_page,
        total: response.data.total,
      });
    } catch (error) {
      message.error('Kullanıcılar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await getBranches({ per_page: 100 });
      setBranches(response.data.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchUsers(1, debouncedSearch, roleFilter, branchFilter);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleValuesChange = (changedValues) => {
    if (isAutoFillingEmail.current) return;

    if ('email' in changedValues) {
      setEmailTouched(true);
      return;
    }

    if (!EMAIL_DOMAIN || emailTouched) return;

    if ('name' in changedValues || 'surname' in changedValues) {
      const { name = '', surname = '' } = form.getFieldsValue(['name', 'surname']);
      isAutoFillingEmail.current = true;
      form.setFieldsValue({ email: toEmailLocalPart(`${name}${surname}`) });
      isAutoFillingEmail.current = false;
    }
  };

  const handleSubmit = async (values) => {
    try {
      const email = EMAIL_DOMAIN ? `${values.email}@${EMAIL_DOMAIN}` : values.email;
      const res = await createUser({ ...values, email });
      // Şifre sunucudan geliyor (istemcide sabit yazılı değil) — şifre
      // sıfırlamadaki gösterimin aynısı.
      // Velide şifre dönmez — hesap şifresiz açılıyor, veli kendi şifresini
      // aktifleştirme formundan kuruyor (bkz. handleResetPassword).
      Modal.success({
        title: 'Kullanıcı Oluşturuldu',
        content: res.data.needs_activation ? (
          <div>
            <p>{values.name} {values.surname} için veli hesabı oluşturuldu.</p>
            <p>{res.data.activation_hint}</p>
          </div>
        ) : (
          <div>
            <p>{values.name} {values.surname} için hesap oluşturuldu.</p>
            <p>Şifre: <strong>{res.data.default_password}</strong></p>
            <p className="text-gray-400 text-sm">Kullanıcı ilk girişte bu şifreyi değiştirmek zorunda kalacak.</p>
          </div>
        ),
      });
      setModalOpen(false);
      form.resetFields();
      setEmailTouched(false);
      fetchUsers();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteUser(id);
      message.success('Kullanıcı silindi.');
      fetchUsers();
    } catch (error) {
      message.error('Kullanıcı silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Ad Soyad',
      key: 'fullname',
      sorter: true,
      sortOrder: sortField === 'fullname' ? sortOrder : null,
      render: (_, record) => (
        <span className="inline-flex items-center gap-2">
          {record.name} {record.surname}
          {/* Portal rozeti YALNIZCA velide: personelin zaten girmesi bekleniyor,
              her satıra yeşil tik koymak rozeti anlamsızlaştırırdı. Asıl soru
              "bu veliye uygulamayı hatırlatmam gerekiyor mu". */}
          {record.role === 'parent' && (
            <VeliPortalRozeti
              girdi={!!record.last_login_at}
              aktiflesti={!!record.activated_at}
              lastLoginAt={record.last_login_at}
            />
          )}
          {/* Takvimdeki ikonun aynısı: metin sunucuda hazırlanıp WhatsApp'a
              taşınıyor, GÖNDER'e yönetici basıyor — uygulama kimseye mesaj
              GÖNDERMİYOR.

              `record.whatsapp` alanını backend yalnızca users.whatsapp izni
              olana ve numarası çözülebilen VELİLERE koyuyor; alan yoksa ikon
              da yok. Burada ayrıca rol kontrolü yapmaya gerek yok — arayüzde
              gizlemek yetki değildir, asıl kapı sunucuda. */}
          {record.whatsapp && (
            <Tooltip title="Veli portalı bilgilendirme mesajı gönder">
              <a
                href={`https://wa.me/${record.whatsapp.phone}?text=${encodeURIComponent(record.whatsapp.text)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: '#25D366', lineHeight: 1 }}
                aria-label={`${record.name} ${record.surname} kullanıcısına WhatsApp mesajı gönder`}
              >
                <WhatsAppOutlined style={{ fontSize: 15 }} />
              </a>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortOrder: sortField === 'email' ? sortOrder : null,
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      sorter: true,
      sortOrder: sortField === 'phone' ? sortOrder : null,
      // Kayıtlı biçim ülke kodlu ve bitişik ('905551112233'); ham gösterilirse
      // okunmaz ve yurt dışı numara TR gibi görünür.
      render: (v) => formatPhone(v) || '—',
    },
    {
      // YIL GOSTERILMIYOR, sadece gun/ay - yas herkese acilmasin diye
      // (backend de /birthdays/upcoming'de yil dondurmuyor).
      title: 'Doğum Günü',
      dataIndex: 'birth_date',
      key: 'birth_date',
      width: 130,
      render: (v) => {
        if (!v) return <span className="text-gray-300">—</span>;

        const kalan = kacGunKaldi(v);
        const yakin = kalan <= 7;

        return (
          <span style={yakin ? { color: '#d46b08', fontWeight: 600 } : undefined}>
            {dayjs(v).format('DD.MM')}
            {kalan === 0 && ' 🎂 bugün'}
            {kalan > 0 && yakin && ` (${kalan} gün)`}
          </span>
        );
      },
    },
    {
      title: 'Şube',
      dataIndex: 'branch_id',
      key: 'branch_id',
      render: (branch_id) => {
        const branch = branches.find(b => b.id === branch_id);
        return branch ? branch.name : <span className="text-gray-400">Tüm Şubeler</span>;
      },
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      key: 'role',
      sorter: true,
      sortOrder: sortField === 'role' ? sortOrder : null,
      render: (role) => (
        <Tag color={roleLabels[role]?.color}>
          {roleLabels[role]?.label || role}
        </Tag>
      ),
    },
    {
      title: 'Eksik Onaylar',
      key: 'missing_kvkk_labels',
      render: (_, record) => {
        const missing = record.missing_kvkk_labels || [];
        if (missing.length === 0) return <span className="text-gray-400">—</span>;
        return (
          <Tag icon={<WarningOutlined />} color="orange" style={{ whiteSpace: 'normal' }}>
            {missing.join(', ')}
          </Tag>
        );
      },
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: true,
      sortOrder: sortField === 'is_active' ? sortOrder : null,
      render: (is_active, record) => {
        if (record.role === 'developer_admin') {
          return <Switch checked disabled checkedChildren="Aktif" unCheckedChildren="Pasif" style={{ backgroundColor: '#52c41a' }} />;
        }
        return (
          <Switch
            checked={is_active}
            checkedChildren="Aktif"
            unCheckedChildren="Pasif"
            style={{ backgroundColor: is_active ? '#52c41a' : '#ff4d4f' }}
            onChange={async (checked) => {
              try {
                await updateUser(record.id, { is_active: checked });
                message.success('Durum güncellendi.');
                fetchUsers();
              } catch (error) {
                message.error('Durum güncellenemedi.');
              }
            }}
          />
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => {
        if (record.role === 'developer_admin' && !isDeveloperAdmin) {
          return <span className="text-gray-400">—</span>;
        }

        return (
          <Space>
            <Button icon={<EyeOutlined />} onClick={() => navigate(`/users/${record.id}`)} size="small">
              Detay
            </Button>
            {canImpersonate && !['developer_admin', ...(isDeveloperAdmin ? [] : ['super_admin'])].includes(record.role) && (
              <Popconfirm
                title={`${record.name} ${record.surname} olarak giriş yapılsın mı?`}
                onConfirm={() => handleImpersonate(record)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button icon={<LoginOutlined />} size="small">
                  Taklit Et
                </Button>
              </Popconfirm>
            )}
            {canResetPasswordOf(record) && (
              <Popconfirm
                title={`${record.name} ${record.surname} kullanıcısının şifresini varsayılan değere sıfırlamak istediğinize emin misiniz?`}
                onConfirm={() => handleResetPassword(record)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button icon={<LockOutlined />} size="small">
                  Şifre Sıfırla
                </Button>
              </Popconfirm>
            )}
            {record.role !== 'developer_admin' && (
              <Popconfirm
                title="Kullanıcıyı silmek istediğinize emin misiniz?"
                onConfirm={() => handleDelete(record.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button icon={<DeleteOutlined />} danger size="small">
                  Sil
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Kullanıcı Yönetimi</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Yeni Kullanıcı
        </Button>
      </div>

      <Card>
        <div className="flex gap-3 mb-4">
          <Input
            placeholder="İsim, e-posta veya telefon ara..."
            allowClear
            style={{ width: 300 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="Rol filtrele"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => {
              setRoleFilter(value);
              fetchUsers(1, debouncedSearch, value, branchFilter);
            }}
          >
            <Option value="branch_manager">Şube Müdürü</Option>
            <Option value="teacher">Öğretmen</Option>
            <Option value="stajyer">Stajyer</Option>
            <Option value="parent">Veli</Option>
          </Select>
          <Select
            placeholder="Şube filtrele"
            allowClear
            style={{ width: 180 }}
            onChange={(value) => {
              setBranchFilter(value);
              fetchUsers(1, debouncedSearch, roleFilter, value);
            }}
          >
            {branches.map((branch) => (
              <Option key={branch.id} value={branch.id}>{branch.name}</Option>
            ))}
          </Select>
          {/*
            Listedeki portal rozetinin filtresi. Eren Bey'in işi: üyeliğini
            hiç kurmamış ya da kurup hiç girmemiş velileri süzüp onlara
            WhatsApp'tan tekrar hatırlatma atmak — 500+ kayıtta rozetlere
            tek tek bakarak bu liste çıkarılamıyordu.

            Seçenekler rozetle AYNI üç durum; metinler de aynı sözlükten
            geliyor ki ekranla filtre birbirinden sapmasın.
            Yalnızca velide anlamlı olduğu için kısıtı backend uyguluyor
            (personelde `activated_at` zaten boş).
          */}
          <Select
            placeholder="Portal durumu (veli)"
            allowClear
            style={{ width: 230 }}
            value={portalFilter}
            onChange={(value) => {
              setPortalFilter(value ?? null);
              // ROL FİLTRESİ TEMİZLENİYOR: bu filtre yalnızca velide
              // anlamlı ve backend zaten `role = parent` uyguluyor. Rol
              // "Öğretmen"de kalsaydı iki koşul çakışır, liste boş gelir ve
              // kullanıcı sebebini anlamazdı.
              const yeniRol = value ? null : roleFilter;
              if (value) setRoleFilter(null);
              fetchUsers(1, debouncedSearch, yeniRol, branchFilter, pagination.pageSize, sortField, sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null, value ?? null);
            }}
          >
            <Option value="girmedi">Üyeliğini hiç kurmamış</Option>
            <Option value="aktif_girmedi">Kurmuş ama hiç girmemiş</Option>
            <Option value="girdi">Giriş yapmış</Option>
          </Select>
          {/*
            ÖĞRENCİ DURUMU — portal filtresiyle BİRLİKTE çalışır (VE).
            Eren Bey listeden WhatsApp atıyor; ayrılmış ya da yeni döneme
            yazılmamış ailelere mesaj gitmesin istiyor (23.09.2026).

            Üç seçenek birbirini dışlıyor ve her biri ayrı bir mesajın
            hedefi: kayıtlı olanlara "bildirim/ana ekrana ekle",
            kayıtsızlara "yeni döneme kaydolun", pasiflere hiçbir şey.
            Bu yüzden iki seçenek yerine üç — "kaydı yok veya pasif" tek
            kovada birleşseydi ilk ikisi ayrıştırılamazdı.
          */}
          <Select
            placeholder="Öğrenci durumu (veli)"
            allowClear
            style={{ width: 240 }}
            value={ogrenciFilter}
            onChange={(value) => {
              setOgrenciFilter(value ?? null);
              const yeniRol = value ? null : roleFilter;
              if (value) setRoleFilter(null);
              fetchUsers(1, debouncedSearch, yeniRol, branchFilter, pagination.pageSize, sortField, sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null, portalFilter, value ?? null);
            }}
          >
            <Option value="aktif_kayitli">Aktif ve bu döneme kayıtlı</Option>
            <Option value="aktif_kayitsiz">Aktif ama bu döneme kaydı yok</Option>
            <Option value="pasif">Pasif / öğrencisi yok</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          onChange={(paginationInfo, _filters, sorter) => {
            const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
            setPersistedPageSize(paginationInfo.pageSize);
            fetchUsers(paginationInfo.current, debouncedSearch, roleFilter, branchFilter, paginationInfo.pageSize, sField, sDir);
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            showTotal: (total) => `Toplam ${total} kullanıcı`,
          }}
        />
      </Card>

      <Modal
        title="Yeni Kullanıcı"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setEmailTouched(false);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleValuesChange}>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="name" label="Ad" rules={[{ required: true, message: 'Ad giriniz.' }]}>
              <Input placeholder="Ad" maxLength={100} />
            </Form.Item>
            <Form.Item name="surname" label="Soyad" rules={[{ required: true, message: 'Soyad giriniz.' }]}>
              <Input placeholder="Soyad" maxLength={100} />
            </Form.Item>
          </div>
          {EMAIL_DOMAIN ? (
            <Form.Item
              name="email"
              label="E-posta"
              rules={[
                { required: true, message: 'E-posta giriniz.' },
                { pattern: /^[^@\s]+$/, message: '@ işareti olmadan sadece kullanıcı adını giriniz.' },
              ]}
            >
              <Input placeholder="adsoyad" addonAfter={`@${EMAIL_DOMAIN}`} maxLength={150} />
            </Form.Item>
          ) : (
            <Form.Item
              name="email"
              label="E-posta"
              rules={[
                { required: true, message: 'E-posta giriniz.' },
                { type: 'email', message: 'Geçerli e-posta giriniz.' },
              ]}
            >
              <Input placeholder="ornek@email.com" maxLength={150} />
            </Form.Item>
          )}
          <Form.Item
            name="phone"
            label="Telefon"
            rules={[{ pattern: /^\+\d{8,15}$/, message: 'Ülke kodunu seçip numarayı giriniz.' }]}
          >
            <PhoneInput />
          </Form.Item>
          <Form.Item name="role" label="Rol" rules={[{ required: true, message: 'Rol seçiniz.' }]}>
            <Select placeholder="Rol seçiniz">
              {isDeveloperAdmin && <Option value="super_admin">Süper Admin</Option>}
              <Option value="branch_manager">Şube Müdürü</Option>
              <Option value="teacher">Öğretmen</Option>
              <Option value="stajyer">Stajyer</Option>
              <Option value="parent">Veli</Option>
            </Select>
          </Form.Item>
          <Form.Item name="branch_id" label="Şube">
            <Select placeholder="Şube seçiniz" allowClear>
              {branches.map((branch) => (
                <Option key={branch.id} value={branch.id}>{branch.name}</Option>
              ))}
            </Select>
          </Form.Item>
          {/* Günlük ücret yalnızca stajyerde anlamlı. Backend ayrıca
              interns.payments.manage izni arıyor — öğretmen bu alanı
              gönderse bile yok sayılıyor (formda gizlemek yetki değildir). */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
            {({ getFieldValue }) =>
              getFieldValue('role') === 'stajyer' ? (
                <Form.Item
                  name="daily_wage"
                  label="Günlük Ücret"
                  extra="Stajyerin geldiği gün başına alacağı tutar. Onaylanan günlere o günkü ücret yazılır; sonradan değiştirmek geçmiş günleri etkilemez."
                >
                  <InputNumber min={0} style={{ width: '100%' }} addonAfter="₺" placeholder="Örn. 500" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item>
            {/* Şifre değeri BİLEREK burada yazmıyor: REACT_APP_* değerleri
                derlenmiş pakete gömülür ve siteyi açan herkes okuyabilir.
                Değer artık sunucudan geliyor, kayıt sonrası gösteriliyor. */}
            <div className="text-gray-400 text-sm">
              Kullanıcıya varsayılan bir şifre atanacak ve kayıttan sonra burada gösterilecek.
              Kullanıcı ilk girişte değiştirmek zorunda kalacak.
            </div>
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => {
                setModalOpen(false);
                form.resetFields();
              }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit">
                Oluştur
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
